import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
  Avatar,
  Chip,
  Badge,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AppsIcon from '@mui/icons-material/Apps';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DownloadIcon from '@mui/icons-material/Download';
import ExtensionIcon from '@mui/icons-material/Extension';
import SupportIcon from '@mui/icons-material/Support';
import UpdateIcon from '@mui/icons-material/Update';
import WebIcon from '@mui/icons-material/Web';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarIcon from '@mui/icons-material/Star';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PhoneIcon from '@mui/icons-material/Phone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link } from 'react-router-dom';
import { getUserProfile, getCustomerApps, CustomerApp } from '../firebase/services';
import { getTechStackById } from '../utils/techStacks';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Application interface
interface Application {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'active' | 'pending' | 'expired' | 'inactive' | 'development' | 'maintenance';
  purchaseDate?: string;
  expiryDate?: string;
  version?: string;
  urlApp?: string;
  category?: string;
  techStack?: string;
  stacks?: string[]; // IDs des stacks technologiques
  link?: string;
  logoUrl?: string; // URL du logo de l'application
  downloads?: PlatformDownload[]; // Ajout des téléchargements multi-plateformes
}

interface PlatformDownload {
  platform: 'ios' | 'android' | 'windows' | 'linux' | 'web';
  downloadUrl: string;
  version: string;
  status: 'active' | 'deprecated' | 'beta' | 'development';
  updatedAt?: any;
  history?: PlatformVersionHistory[];
}

interface PlatformVersionHistory {
  version: string;
  downloadUrl: string;
  status: 'active' | 'deprecated' | 'beta' | 'development';
  releaseDate: any;
  notes?: string;
}

// Fonction pour formater une date en toLocaleDateString de manière sécurisée
const formatDate = (date: any): string => {
  if (!date) return '';
  
  try {
    // Si c'est déjà une Date
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    
    // Si c'est une chaîne, essayer de la convertir
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    
    // Si c'est un timestamp Firebase
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toLocaleDateString();
    }
    
    return '';
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return '';
  }
};

// Mise à jour de l'interface CustomerApp dans le fichier firebase/services.ts
// Cela doit être fait dans un autre fichier, mais pour l'instant, étendons l'interface localement
interface ExtendedCustomerApp extends CustomerApp {
  stacks?: string[];
  downloads?: PlatformDownload[];
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`myhub-tabpanel-${index}`}
      aria-labelledby={`myhub-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const MyHub: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // État pour le menu de téléchargement
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentApp, setCurrentApp] = useState<Application | null>(null);
  
  // Helper function to get string value from translation
  const getTranslationString = useCallback((key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  }, [t]);

  // Fonction pour obtenir l'icône appropriée pour une application en fonction de sa catégorie
  const getAppIcon = (app: CustomerApp): React.ReactNode => {
    switch (app.category) {
      case 'webApp':
        return <WebIcon fontSize="large" />;
      case 'api':
        return <CloudIcon fontSize="large" />;
      case 'desktop':
        return <StorageIcon fontSize="large" />;
      case 'mobileApp':
        return <PhoneIcon fontSize="large" />;
      case 'security':
        return <SecurityIcon fontSize="large" />;
      default:
        return <AppsIcon fontSize="large" />;
    }
  };

  // Ajouter une fonction pour vérifier si une URL est une image
  const isImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    // Vérifier les extensions d'image communes
    const hasImageExtension = /\.(jpg|jpeg|png|gif|bmp|svg|webp|ico)$/i.test(url);
    
    // Vérifier les services d'hébergement d'images ou les chemins courants
    const isImageHosting = url.includes('image-heberg') || 
                           url.includes('imgur') ||
                           url.includes('cloudinary') ||
                           url.includes('images') ||
                           url.includes('img') ||
                           url.includes('logo') ||
                           url.includes('icon') ||
                           url.includes('avatar') ||
                           url.includes('photo');
    
    // Vérifier si l'URL contient des paramètres d'image courants
    const hasImageParams = url.includes('format=') || 
                           url.includes('width=') || 
                           url.includes('height=') || 
                           url.includes('w=') || 
                           url.includes('h=');
    
    return hasImageExtension || isImageHosting || hasImageParams;
  };

  // Améliorer la gestion des logos dans la fonction formatApps
  const formatApps = (apps: CustomerApp[]): Application[] => {
    return apps.map(app => {
      // Cast en ExtendedCustomerApp pour accéder aux nouvelles propriétés
      const extendedApp = app as ExtendedCustomerApp;
      
      // Pour le débogage
      console.log('App data:', {
        name: app.name,
        urlApp: app.urlApp,
        icon: app.icon
      });
      
      // Détermination du logo et de l'URL
      let logoUrl = '';
      let appUrl = '';
      
      // Si urlApp est une image, l'utiliser comme logo
      if (app.urlApp && isImageUrl(app.urlApp)) {
        logoUrl = app.urlApp;
        console.log(`Using urlApp as logo for ${app.name}:`, app.urlApp);
      } 
      // Sinon, si icon est disponible comme chaîne, l'utiliser comme logo
      else if (app.icon && typeof app.icon === 'string') {
        logoUrl = app.icon;
        console.log(`Using icon as logo for ${app.name}:`, app.icon);
      }
      
      // Si urlApp n'est pas une image, l'utiliser comme URL de l'application
      if (app.urlApp && !isImageUrl(app.urlApp)) {
        appUrl = app.urlApp;
      } else if (app.link) {
        appUrl = app.link;
      }
      
      // Si toujours pas de logo, générer un avatar basé sur les initiales
      if (!logoUrl && app.name) {
        const initials = app.name.split(' ').map(word => word[0]).join('').toUpperCase();
        logoUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=100`;
        console.log(`Generated avatar for ${app.name}:`, logoUrl);
      }
      
      return {
        id: app.id,
        name: app.name,
        description: app.description || '',
        status: app.status,
        icon: getAppIcon(app),
        version: app.stage || '1.0',
        urlApp: appUrl,
        category: app.category || '',
        techStack: app.techStack || '',
        stacks: extendedApp.stacks || [],
        link: app.link || '',
        logoUrl: logoUrl,
        downloads: extendedApp.downloads || [],
        purchaseDate: app.createdAt ? formatDate(app.createdAt) : formatDate(new Date()),
        expiryDate: app.updatedAt ? formatDate(new Date((app.updatedAt.seconds || 0) * 1000 + 31536000000)) : formatDate(new Date(Date.now() + 31536000000)) // +1 an
      };
    });
  };

  useEffect(() => {
    // Fetch user data and applications from Firebase
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        // Get user profile
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        
        // Set user data
        setUserData({
          name: currentUser?.displayName || 'User',
          email: currentUser?.email,
          joinDate: new Date().toLocaleDateString(),
          tickets: [
            { id: 'TK-1001', title: 'Account Access Issue', status: 'open', date: '2023-10-15' },
            { id: 'TK-1002', title: 'Billing Question', status: 'closed', date: '2023-09-28' },
          ],
          notifications: [
            { id: 1, message: getTranslationString('myhub.notifications.welcome'), read: false, date: '2023-10-20' },
            { id: 2, message: getTranslationString('myhub.notifications.ticket'), read: true, date: '2023-10-18' },
          ]
        });

        // If user is associated with a customer, get customer apps
        if (profile?.customerId) {
          const customerApps = await getCustomerApps(profile.customerId);
          const formattedApps = formatApps(customerApps);
          setApplications(formattedApps);
        } else {
          // No customer associated, use empty array
          setApplications([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, getTranslationString]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'expired':
        return 'error';
      case 'development':
        return 'info';
      case 'maintenance':
        return 'warning';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  // Ouvrir le menu de téléchargement
  const handleOpenDownloadMenu = (event: React.MouseEvent<HTMLElement>, app: Application) => {
    event.stopPropagation();
    setDownloadMenuAnchor(event.currentTarget);
    setCurrentApp(app);
  };
  
  // Fermer le menu de téléchargement
  const handleCloseDownloadMenu = () => {
    setDownloadMenuAnchor(null);
  };

  // Remplacer la fonction getPlatformLogo pour utiliser les logos locaux
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'ios':
        return '/resources/logo/applestore.png';
      case 'android':
        return '/resources/logo/playstore.png';
      case 'windows':
        return '/resources/logo/windows.png';
      case 'linux':
        return '/resources/logo/linux.png';
      case 'web':
        return '/resources/logo/web.png';
      default:
        return '/resources/logo/web.png';
    }
  };

  // Obtenir le nom de la plateforme
  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'ios':
        return 'iOS (App Store)';
      case 'android':
        return 'Android (Google Play)';
      case 'windows':
        return 'Windows';
      case 'linux':
        return 'Linux';
      case 'web':
        return 'Web App';
      default:
        return platform;
    }
  };
  
  // Obtenir la couleur de l'état de la plateforme
  const getPlatformStatusColor = (status: string | undefined) => {
    if (!status) return 'success'; // Par défaut, retourner 'success' si status est undefined
    
    switch (status) {
      case 'active':
        return 'success';
      case 'deprecated':
        return 'error';
      case 'beta':
        return 'warning';
      case 'development':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography ml={2}>{getTranslationString('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 2,
            background: 'url(/resources/myhub.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.14)',
              zIndex: 0
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                color: '#ffffff',
                textShadow: '1px 1px 3px rgba(0, 0, 0, 0.7)'
              }}
            >
              {getTranslationString('myhub.welcome', 'Welcome')}, {userData.name}!
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, color: '#ffffff', textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}>
              {getTranslationString('myhub.subtitle', 'Manage your applications, subscriptions, and settings in one place.')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ShoppingBagIcon />}
                sx={{
                  bgcolor: 'white',
                  color: '#015480',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
              >
                {getTranslationString('myhub.browseApps', 'Browse Applications')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SupportIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.7)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                {getTranslationString('myhub.support', 'Get Support')}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="myhub tabs"
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              icon={<AppsIcon />} 
              iconPosition="start" 
              label={getTranslationString('myhub.tabs.applications', 'My Applications')} 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<ShoppingBagIcon />} 
              iconPosition="start" 
              label={getTranslationString('myhub.tabs.purchases', 'Purchase History')} 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<StorefrontIcon />} 
              iconPosition="start" 
              label={getTranslationString('myhub.tabs.marketplace', 'Marketplace')} 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<SettingsIcon />} 
              iconPosition="start" 
              label={getTranslationString('myhub.tabs.settings', 'Settings')} 
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {/* Applications Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AppsIcon />
              {getTranslationString('myhub.applications.title', 'Your Applications')}
            </Typography>
            
            {applications.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
              {applications.map((app) => (
                <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 1.5 }} key={app.id}>
                  <Card 
                    elevation={0} 
                    sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4
                      }
                    }}
                  >
                      {/* Bannière avec logo et statut */}
                      <Box
                          sx={{ 
                          position: 'relative',
                          height: 120,
                          background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Cercles décoratifs en fond */}
                        <Box
                          sx={{
                            position: 'absolute',
                            width: 180,
                            height: 180,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.1)',
                            top: -60,
                            right: -60
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.15)',
                            bottom: -30,
                            left: 20
                          }}
                        />
                        
                        {/* Logo de l'application avec gestion des erreurs */}
                        <Box sx={{ position: 'relative', textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid white',
                              boxShadow: 3,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            bgcolor: 'primary.light', 
                              margin: '0 auto'
                            }}
                          >
                            {app.logoUrl ? (
                              <Box
                                component="img"
                                src={app.logoUrl}
                                alt={app.name}
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                  // Si l'image ne se charge pas, remplacer par une icône par défaut
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    // Créer une div avec l'initiale du nom
                                    const initial = document.createElement('div');
                                    initial.textContent = app.name[0].toUpperCase();
                                    initial.style.fontSize = '2rem';
                                    initial.style.fontWeight = 'bold';
                                    initial.style.color = 'white';
                                    parent.appendChild(initial);
                                  }
                                }}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              // Afficher l'initiale du nom si pas de logo
                              <Typography variant="h4" color="white" fontWeight="bold">
                                {app.name[0].toUpperCase()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        
                        {/* Badge de statut */}
                        <Chip 
                          label={app.status.charAt(0).toUpperCase() + app.status.slice(1)} 
                          color={getStatusColor(app.status)} 
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: 16, 
                            right: 16,
                            fontWeight: 'medium',
                            boxShadow: 1
                          }}
                        />
                    </Box>
                    
                      {/* Contenu principal */}
                      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
                        {app.name}
                      </Typography>
                      
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            flexGrow: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                        {app.description}
                      </Typography>
                  
                        {/* Informations de version et expiration */}
                        <Box sx={{ mb: 2 }}>
                          {app.version && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <UpdateIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                          {getTranslationString('myhub.applications.version', 'Version')}: {app.version}
                      </Typography>
                    </Box>
                          )}
                    
                      {app.expiryDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ExtensionIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                            {getTranslationString('myhub.applications.expires', 'Expires')}: {app.expiryDate}
                      </Typography>
                    </Box>
                      )}
                        </Box>
                        
                        {/* Affichage des stacks technologiques */}
                        {app.stacks && app.stacks.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                              {getTranslationString('myhub.applications.techStack', 'Tech Stack')}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {app.stacks.slice(0, 5).map(stackId => {
                                const stack = getTechStackById(stackId);
                                if (!stack) return null;
                                
                                return (
                                  <Chip
                                    key={stackId}
                                    label={stack.name}
                                    size="small"
                                    sx={{ 
                                      bgcolor: `${stack.color}22`,
                                      borderColor: stack.color,
                                      border: '1px solid',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                );
                              })}
                              {app.stacks.length > 5 && (
                                <Chip
                                  label={`+${app.stacks.length - 5}`}
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Affichage des plateformes disponibles - version améliorée */}
                        {(app.downloads && app.downloads.length > 0) || app.urlApp ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                              {getTranslationString('myhub.applications.availablePlatforms', 'Available Platforms')}:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {/* Web URL si disponible */}
                              {app.urlApp && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 1,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    textDecoration: 'none',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      width: '30%',
                                      height: '100%',
                                      background: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.08))',
                                      opacity: 0.5,
                                      zIndex: 0
                                    },
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: 3,
                                      borderColor: 'primary.main',
                                      '& .launch-button': {
                                        bgcolor: 'primary.dark',
                                        transform: 'scale(1.05)'
                                      }
                                    }
                                  }}
                                  component="a"
                                  href={app.urlApp}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Box
                                    component="img"
                                    src="/resources/logo/web.png"
                                    alt="Web Application"
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      objectFit: 'contain',
                                      p: 0.5
                                    }}
                                  />
                                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium" 
                                      sx={{ 
                                        mb: 0.5,
                                        color: theme.palette.mode === 'dark' 
                                          ? 'primary.light' 
                                          : 'primary.dark'
                                      }}
                                    >
                                      Web Application
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {app.urlApp.replace(/^https?:\/\/(www\.)?/i, '')}
                                    </Typography>
                                  </Box>
                  <Button 
                                    className="launch-button"
                          variant="contained" 
                                    size="small"
                    color="primary" 
                                    startIcon={<WebIcon fontSize="small" />}
                                    sx={{ 
                                      borderRadius: 6, 
                                      px: 2,
                                      minWidth: 'auto',
                                      transition: 'all 0.3s ease',
                                      boxShadow: 2
                                    }}
                                  >
                                    {getTranslationString('common.launch', 'Launch')}
                  </Button>
                                </Box>
                              )}

                              {/* Téléchargements des différentes plateformes */}
                              {app.downloads && app.downloads.map((download, index) => (
                                <Box 
                                  key={index} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 1,
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    textDecoration: 'none',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      width: '30%',
                                      height: '100%',
                                      background: `linear-gradient(90deg, transparent, ${getPlatformStatusColor(download.status || 'active') + '.light'}22)`,
                                      opacity: 0.5,
                                      zIndex: 0
                                    },
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: 3,
                                      borderColor: getPlatformStatusColor(download.status || 'active') + '.main',
                                      '& .download-button': {
                                        bgcolor: getPlatformStatusColor(download.status || 'active') + '.main',
                                        transform: 'scale(1.05)'
                                      }
                                    }
                                  }}
                                  component="a"
                                  href={download.downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Box
                                    component="img"
                                    src={getPlatformLogo(download.platform)}
                                    alt={download.platform}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      objectFit: 'contain',
                                      p: 0.5
                                    }}
                                  />
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium" 
                                      sx={{ 
                                        mb: 0.5,
                                        color: theme.palette.mode === 'dark' 
                                          ? `${getPlatformStatusColor(download.status || 'active')}.light` 
                                          : `${getPlatformStatusColor(download.status || 'active')}.dark`
                                      }}
                                    >
                                      {getPlatformName(download.platform)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        v{download.version || '1.0'}
                                      </Typography>
                                      <Chip 
                                        label={download.status ? (download.status.charAt(0).toUpperCase() + download.status.slice(1)) : 'Active'} 
                                        size="small"
                                        color={getPlatformStatusColor(download.status || 'active') as any}
                                        sx={{ height: 18, '& .MuiChip-label': { px: 0.8, py: 0.1, fontSize: '0.65rem' } }}
                                      />
                                    </Box>
                                  </Box>
                  <Button 
                                    className="download-button"
                                    variant="contained"
                                    size="small"
                                    color={getPlatformStatusColor(download.status || 'active') as any}
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    sx={{ 
                                      borderRadius: 6, 
                                      px: 2,
                                      minWidth: 'auto',
                                      transition: 'all 0.3s ease',
                                      boxShadow: 2
                                    }}
                                  >
                                    {getTranslationString('common.download', 'Download')}
                  </Button>
                      </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
            ) : (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {getTranslationString('myhub.applications.none', 'No applications found')}
                </Typography>
              </Paper>
            )}
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Paper elevation={0} sx={{ 
              p: 3, 
              borderRadius: 2,
              background: 'url(/resources/canvas3.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                zIndex: 0
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    {getTranslationString('myhub.applications.discover', 'Discover More Applications')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, color: 'white' }}>
                    {getTranslationString('myhub.applications.discoverDesc', 'Explore our marketplace for more business solutions')}
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<StorefrontIcon />}
                  sx={{ 
                    bgcolor: 'white',
                    color: '#015480',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                >
                  {getTranslationString('myhub.applications.viewMarketplace', 'View Marketplace')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </TabPanel>

        {/* Purchase History Tab */}
        <TabPanel value={tabValue} index={1}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider' 
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <ShoppingBagIcon />
              {getTranslationString('myhub.purchases.title', 'Your Purchase History')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {applications.length > 0 ? (
              <Box>
                {applications.map((app) => (
                  <Card 
                    key={app.id} 
                    variant="outlined" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 3,
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            {app.icon}
                          </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                              {app.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                              {getTranslationString('myhub.purchases.purchased', 'Purchased')}: {app.purchaseDate || 'N/A'}
                          </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip 
                            label={app.status.charAt(0).toUpperCase() + app.status.slice(1)} 
                            color={getStatusColor(app.status)} 
                            size="small"
                          />
                          <Button 
                            variant="outlined" 
                            size="small"
                            sx={{ borderRadius: 2 }}
                          >
                            {getTranslationString('myhub.purchases.details', 'Details')}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
                {getTranslationString('myhub.purchases.none', 'No purchase history found')}
              </Typography>
            )}
          </Paper>
        </TabPanel>

        {/* Marketplace Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <StorefrontIcon />
              {getTranslationString('myhub.marketplace.title', 'Application Marketplace')}
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 4, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'warning.light',
                bgcolor: 'rgba(255, 152, 0, 0.05)' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StorefrontIcon sx={{ color: 'warning.main', fontSize: 32 }} />
              <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'warning.main' }}>
                    {getTranslationString('myhub.marketplace.discover', 'Discover New Applications')}
                  </Typography>
                  <Typography variant="body2">
                    {getTranslationString('myhub.marketplace.discoverDesc', 'Browse our marketplace for business solutions that can help streamline your operations.')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
              {/* Application 1 */}
              <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 1.5 }}>
                <Card elevation={1} sx={{ height: '100%', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56, boxShadow: 2 }}>
                        <WebIcon fontSize="large" />
                      </Avatar>
                      <Chip label="Premium" color="warning" size="small" />
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Advanced Analytics
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Powerful analytics tool with customizable dashboards and real-time data visualization.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <StarIcon 
                            key={rating} 
                            fontSize="small" 
                            sx={{ color: rating <= 4 ? 'orange' : 'rgba(0, 0, 0, 0.2)', width: 18, height: 18 }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        (42)
                      </Typography>
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      $29.99/mo
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="warning" 
                        startIcon={<ShoppingCartIcon />}
                        fullWidth
                        sx={{ borderRadius: 2 }}
                      >
                        {getTranslationString('myhub.marketplace.addToCart', 'Add to Cart')}
                      </Button>
                      <Button 
                    variant="outlined" 
                        color="warning"
                        sx={{ borderRadius: 2 }}
                      >
                        <DownloadIcon />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Application 2 */}
              <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 1.5 }}>
                <Card elevation={1} sx={{ height: '100%', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56, boxShadow: 2 }}>
                        <StorageIcon fontSize="large" />
                      </Avatar>
                      <Chip label="Free" color="success" size="small" />
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Data Backup
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Simple and reliable data backup solution with automatic scheduling and encryption.
                          </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <StarIcon 
                            key={rating} 
                            fontSize="small" 
                            sx={{ color: rating <= 5 ? 'orange' : 'rgba(0, 0, 0, 0.2)', width: 18, height: 18 }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        (128)
                          </Typography>
                        </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Free
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        fullWidth
                        sx={{ borderRadius: 2 }}
                      >
                        {getTranslationString('myhub.marketplace.install', 'Install Now')}
                      </Button>
                      <Button 
                        variant="outlined"
                        color="success"
                        sx={{ borderRadius: 2 }}
                      >
                        <DownloadIcon />
                          </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Application 3 */}
              <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, p: 1.5 }}>
                <Card elevation={1} sx={{ height: '100%', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56, boxShadow: 2 }}>
                        <SecurityIcon fontSize="large" />
                      </Avatar>
                      <Chip label="Premium" color="warning" size="small" />
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Security Suite
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Comprehensive security solution with threat detection, vulnerability scanning and more.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <StarIcon 
                            key={rating} 
                            fontSize="small" 
                            sx={{ color: rating <= 4.5 ? 'orange' : 'rgba(0, 0, 0, 0.2)', width: 18, height: 18 }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        (86)
                      </Typography>
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      $49.99/mo
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="warning" 
                        startIcon={<ShoppingCartIcon />}
                        fullWidth
                        sx={{ borderRadius: 2 }}
                      >
                        {getTranslationString('myhub.marketplace.addToCart', 'Add to Cart')}
                      </Button>
                      <Button 
                        variant="outlined"
                        color="warning"
                        sx={{ borderRadius: 2 }}
                      >
                        <DownloadIcon />
                      </Button>
                      </Box>
                    </CardContent>
                  </Card>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={3}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider' 
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <SettingsIcon />
              {getTranslationString('myhub.settings.title', 'Account Settings')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
              <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {getTranslationString('myhub.settings.profile', 'Profile Information')}
                </Typography>
                <Button variant="contained" color="primary" sx={{ mb: 3 }}>
                  {getTranslationString('myhub.settings.updateProfile', 'Update Profile')}
                </Button>
                
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 3 }}>
                  {getTranslationString('myhub.settings.password', 'Password')}
                </Typography>
                <Button variant="outlined" color="primary">
                  {getTranslationString('myhub.settings.changePassword', 'Change Password')}
                </Button>
              </Box>
              
              <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {getTranslationString('myhub.settings.notifications', 'Notification Preferences')}
                </Typography>
                <Button variant="outlined" color="primary" sx={{ mb: 3 }}>
                  {getTranslationString('myhub.settings.manageNotifications', 'Manage Notifications')}
                </Button>
                
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 3 }}>
                  {getTranslationString('myhub.settings.data', 'Your Data')}
                </Typography>
                <Button variant="outlined" color="error">
                  {getTranslationString('myhub.settings.downloadData', 'Download Your Data')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </TabPanel>
      </Container>
    </Box>
  );
};

export default MyHub; 